import { Alert, Box, Link, Typography } from "@mui/material";
import { TotpMultiFactorGenerator } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../../components/common/BriaInput/BriaInput";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import { AESCipher } from "../../../../helpers/encryption.ts";
import usePostHog from "../../../../hooks/usePostHog.tsx";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import classes from "../Login.module.scss";
import styles from "./MfaVerification.module.scss";

interface MfaVerificationProps {
	mfaResolver: any;
	continueUrl?: string;
	state?: any;
	onBackToLogin: () => void;
	navigate: (path: string, options?: { state?: any }) => void;
	email: string;
	authMethod: string;
}

const MfaVerification: React.FC<MfaVerificationProps> = ({
	mfaResolver,
	continueUrl,
	state,
	onBackToLogin,
	navigate,
	email,
	authMethod,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "mfaLoginVerification" });
	const { trackEvent } = usePostHog();
	const [mfaCode, setMfaCode] = useState("");
	const [mfaError, setMfaError] = useState("");
	const [mfaLoading, setMfaLoading] = useState(false);

	const { authStore } = useAppStore();
	const [isLocked, setIsLocked] = useState(false);
	const [lockedUntil, setLockedUntil] = useState<Date | null>(null);

	const [countdown, setCountdown] = useState("");
	const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

	useEffect(() => {
		setMfaError("");
	}, [mfaResolver]);

	useEffect(() => {
		if (!isLocked || !lockedUntil) {
			setCountdown("");
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			return;
		}

		function updateCountdown() {
			const now = new Date();
			const lockedDate = new Date(lockedUntil + "Z");
			let diff = Math.floor((lockedDate.getTime() - now.getTime()) / 1000);

			if (diff <= 0) {
				setIsLocked(false);
				setCountdown("");
				clearInterval(intervalRef?.current);
			} else {
				const minutes = Math.floor(diff / 60)
					.toString()
					.padStart(2, "0");
				const seconds = (diff % 60).toString().padStart(2, "0");
				setCountdown(`${minutes}:${seconds}`);
			}
		}

		updateCountdown();
		intervalRef.current = setInterval(updateCountdown, 1000);

		return () => clearInterval(intervalRef.current);
	}, [isLocked, lockedUntil]);

	const handleMfaVerification = async () => {
		if (!mfaResolver) return;
		setMfaLoading(true);
		setMfaError("");
		const aesCipher = new AESCipher();
		const encryptedEmail: string = await aesCipher.encrypt(email);

		try {
			const data = await authStore.checkMfaLock(encryptedEmail);
			if (data.locked) {
				setIsLocked(true);
				setMfaLoading(false);
				setLockedUntil(new Date(data.locked_until));
				const utcIso = data.locked_until + "Z";
				const dateObj = new Date(utcIso);
				const lockedTimeLocal = dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

				setMfaError(t("lockedOutMessage", { time: lockedTimeLocal }));
				return;
			}

			const totpHint = mfaResolver.hints.find((hint: any) => hint.factorId === "totp");
			if (!totpHint) throw new Error("No TOTP factor enrolled");

			const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, mfaCode);
			await mfaResolver.resolveSignIn(assertion);
			await authStore.createMfaAttempt(true, encryptedEmail);

			trackEvent(POSTHOG_EVENTS.LOGIN_SUCCESSFUL, {
				method: authMethod,
				email: email,
				mfa_verified: true,
			});

			if (continueUrl) {
				const formattedUrl = continueUrl.startsWith("/") ? continueUrl : `/${continueUrl}`;
				navigate(formattedUrl, { state });
			} else {
				window.location.reload();
			}
		} catch (e: any) {
			if (e.code === "auth/invalid-verification-code" || e.code === "auth/code-expired") {
				setMfaError(t("firebaseErrors.codeError"));
				await authStore.createMfaAttempt(false, encryptedEmail);
			} else if (e.code == "auth/totp-challenge-timeout") {
				setMfaError(t("firebaseErrors.expiredVerification"));
			} else if (e.code == "auth/request-contains-an-invalid-argument.") {
				setMfaError(t("firebaseErrors.missingVerificationCode"));
			} else {
				setMfaError(t("genericError"));
			}
		} finally {
			setMfaLoading(false);
		}
	};

	return (
		<Box className={styles.mfaContainer}>
			<Typography className={styles.mfaTitle}>{t("enterCodeText")}</Typography>

			{mfaError && (
				<Alert className={styles.mfaError} severity="error">
					{mfaError}
				</Alert>
			)}

			<BriaInput
				id="mfa-code"
				label={t("verificationCodeLabel")}
				value={mfaCode}
				onChange={(e) => {
					const digitsOnly = e.target.value.replace(/\D/g, "");
					setMfaCode(digitsOnly);
				}}
				onEnterPress={() => mfaCode && handleMfaVerification()}
				className={styles.textField}
			/>

			<BriaButton
				buttonType="primary"
				onClick={handleMfaVerification}
				disabled={mfaLoading || isLocked || !mfaCode}
				loading={mfaLoading}
				className={styles.mfaVerifyButton}
			>
				{isLocked && countdown ? countdown : t("verifyCodeButton")}
			</BriaButton>

			<Box className={styles.backToLoginLink}>
				<Link onClick={onBackToLogin}>{t("switchToLoginText")}</Link>
			</Box>
			<Typography className={classes.alreadyHaveAnAccount}>
				{t("contactUs.text")}
				<Link
					href={LinkConstants.BRIA_CONTACT_US_MFA}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.contactLink}
				>
					{t("contactUs.link")}
				</Link>
			</Typography>
		</Box>
	);
};

export default MfaVerification;
